import { pickBy } from 'lodash';
import { memo, useMemo, useState } from 'react';

import { ShortUrlObjectType } from '@air/api/types';
import { Tabs } from '@air/primitive-tabs';

import { ShareLinkModalFooter } from '~/components/ShareModal/components/ShareLinkModalFooter';
import { ShareLinkModal } from '~/components/ShareModal/ShareLinkModal';
import { ShareLinkTeamInviteNudgePanel } from '~/components/SharePrivateContentModal/ShareLinkTeamInviteNudgePanel';
import { CreateShowcaseDesignTab } from '~/components/ShowcaseModal/CreateShowcaseDesignTab';
import { CreateShowcaseModalBody } from '~/components/ShowcaseModal/CreateShowcaseModalBody';
import { CreateShowcaseModalFooter } from '~/components/ShowcaseModal/CreateShowcaseModalFooter';
import { SelectedItemListSummary } from '~/components/ShowcaseModal/components/SelectedItemListSummary';
import {
  DEFAULT_PERMISSIONS,
  type ShowcaseCreated,
  useCreateShowcaseModalState,
} from '~/components/ShowcaseModal/hooks/useCreateShowcaseModalState';
import { ShowcaseModalProvider } from '~/components/ShowcaseModal/ShowcaseModalContext';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetDefaultSettings } from '~/swr-hooks/shortUrl/useGetDefaultSettings';
import { canCreateCollection } from '~/utils/permissions/workspacePermissions';

export type CreateShowcaseModalProps = {
  onClose: () => void;
  assetIds: string[];
  boardIds: string[];
  /**
   * Hands the new showcase to the entry point that opened this modal, which owns what happens next:
   * the Showcases page takes the user straight into the editor, while the library share menu and the
   * selection bar leave the user where they were and show a confirmation toast.
   */
  onShowcaseCreated: (created: ShowcaseCreated) => void;
};

export const CreateShowcaseModal = memo(
  ({ onClose, assetIds, boardIds, onShowcaseCreated }: CreateShowcaseModalProps) => {
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const { currentWorkspace } = useCurrentWorkspace();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const [activeTab, setActiveTab] = useState('settings');
    const {
      isSuccess: hasLoadedDefaultSettings,
      isError: hasFailedDefaultSettings,
      data: defaultSettings,
    } = useGetDefaultSettings({
      workspaceId: currentWorkspace?.id ?? '',
      objectType: ShortUrlObjectType.collection,
    });
    /**
     * The DESIGN defaults live on the workspace-wide (typeless) record — the one the "Showcase
     * defaults" settings page writes — and the collection-scoped GET above does NOT fold them in
     * (QA-verified: the modal showed the system look while the created showcase inherited the
     * defaults server-side). Permissions keep reading the scoped record above, exactly as before.
     */
    const {
      isSuccess: hasLoadedWorkspaceDefaultSettings,
      isError: hasFailedWorkspaceDefaultSettings,
      data: workspaceDefaultSettings,
    } = useGetDefaultSettings({ workspaceId: currentWorkspace?.id ?? '' });
    /**
     * Success/error — NOT `!isLoading`: the queries are disabled until the workspace id resolves,
     * and a disabled query is not loading, which would read as "settled" before the fetch even
     * starts. Both the render gate below and the design-defaults seed latch on this, so the modal's
     * UI can never mount before the defaults had their one chance to arrive (an errored query
     * renders with the system look — the same degradation the server would apply nothing to).
     */
    const areDefaultSettingsSettled =
      (hasLoadedDefaultSettings || hasFailedDefaultSettings) &&
      (hasLoadedWorkspaceDefaultSettings || hasFailedWorkspaceDefaultSettings);

    const permissions = useMemo(
      () => ({ ...DEFAULT_PERMISSIONS, ...defaultSettings?.permissions }),
      [defaultSettings?.permissions],
    );

    // Workspace-wide design defaults, with any collection-scoped VALUES winning per key should the
    // backend ever store per-type presentation defaults. `pickBy(…, ≠ nullish)` matters: a scoped
    // record serialized with explicit nulls for fields it doesn't store would otherwise clobber the
    // workspace-wide values under a plain spread and silently un-seed the whole Design tab.
    const collectionPresentationSettings = defaultSettings?.presentationSettings;
    const workspacePresentationSettings = workspaceDefaultSettings?.presentationSettings;
    const defaultPresentationSettings = useMemo(
      () =>
        workspacePresentationSettings || collectionPresentationSettings
          ? {
              ...workspacePresentationSettings,
              ...pickBy(collectionPresentationSettings, (value) => value != null),
            }
          : undefined,
      [workspacePresentationSettings, collectionPresentationSettings],
    );

    const props = useCreateShowcaseModalState({
      assetIds,
      boardIds,
      onShowcaseCreated,
      permissions,
      defaultPresentationSettings,
      areDefaultSettingsSettled,
    });

    const { isShareLinkTeamInviteNudgeVisible, openShareLinkTeamInviteNudgeModal, workspaceName } =
      useShowShareLinkTeamInviteNudgeForModal({
        isShareLinkCreated: props.hasCreatedShowcase,
        isShareLinkReady: props.hasCreatedShowcase,
        location: 'collection_share_link',
        onShareLinkCreatedFallback: onClose,
      });

    const contextValue = useMemo(
      () => ({
        ...props,
        isEditMode: false,
        errors: props.validationErrors,
        onSave: props.handleCreate,
        onClose,
      }),
      [props, onClose],
    );

    if (!canCreateCollection(workspacePermissions) || showingSubscriptionExpiredModal || !areDefaultSettingsSettled) {
      return null;
    }

    return (
      <ShowcaseModalProvider value={contextValue}>
        <ShareLinkModal
          data-testid="SHARE_PRIVATE_CONTENT_MODAL"
          accessory={
            isShareLinkTeamInviteNudgeVisible ? (
              <ShareLinkTeamInviteNudgePanel
                onInviteClick={openShareLinkTeamInviteNudgeModal}
                workspaceName={workspaceName}
              />
            ) : undefined
          }
          headerSubtitle={<SelectedItemListSummary />}
          onClose={onClose}
          title="Create showcase"
        >
          <Tabs
            className="flex min-h-0 flex-1 flex-col"
            data-testid="SHARE_PRIVATE_COLLECTION_MODAL_TABS"
            listClassName="shrink-0 border-b ads:border-b-default border-b-gray-4 px-6 pt-1"
            onValueChange={setActiveTab}
            panels={[
              {
                id: 'settings',
                label: 'Settings',
                // The body renders a flat fragment of sections and relies on its container for stack
                // spacing. The flex wrapper lives INSIDE TabsContent (not via contentClassName) so the
                // inactive panel's `hidden` attribute can collapse it to zero height — a display class
                // on TabsContent would override `[hidden] { display:none }` and leave empty padding
                // behind.
                content: (
                  <div className="flex flex-col gap-6">
                    <CreateShowcaseModalBody />
                  </div>
                ),
              },
              {
                id: 'design',
                label: 'Design',
                content: (
                  <CreateShowcaseDesignTab
                    showcaseTitle={props.showcaseProps.name}
                    coverImage={props.draftCoverImage ?? undefined}
                    customColors={props.shortUrlProps.presentationSettings?.customColors}
                    customFonts={props.draftCustomFonts ?? undefined}
                    customLogo={props.draftCustomLogo ?? undefined}
                    // Seeds the tab's preview-only layout/theme controls (never sent at create — the
                    // server applies these same defaults), so the preview shows the link's actual
                    // starting look. Safe to read at mount: the modal renders nothing until the
                    // defaults query settles (the areDefaultSettingsSettled guard above).
                    defaultPresentationSettings={defaultPresentationSettings}
                    isLoading={props.isSaving}
                    // create flow persists on submit — the per-change analytics args are unused
                    onApplyBrandColors={props.updateCustomColors}
                    onCommitColors={props.updateCustomColors}
                    onCommitCoverImage={props.updateCoverImage}
                    onCommitCustomFonts={props.updateCustomFonts}
                    onCommitCustomLogo={props.updateCustomLogo}
                  />
                ),
              },
            ]}
            panelsClassName="min-h-0 overflow-y-auto px-6 pb-4 pt-4"
            value={activeTab}
          />
          <ShareLinkModalFooter>
            <CreateShowcaseModalFooter />
          </ShareLinkModalFooter>
        </ShareLinkModal>
      </ShowcaseModalProvider>
    );
  },
);

CreateShowcaseModal.displayName = 'CreateShowcaseModal';
